import { DevHubButton } from '@devxdiscover/devhub-ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExperimentalOnboardingModal } from './OnboardingModal';
import { ExperimentalOnboardingInfoModal } from './ExperimentalOnboardingInfoModal';

interface LearnMoreButtonProps {
  variant?: 'contained' | 'outlined' | 'text';
  buttonColor?: string;
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  textColor?: string;
  size?: 'small' | 'medium' | 'large';
  children?: React.ReactNode;
}

/**
 * Reusable button component for learning about experimental onboarding
 * Always shows info modal first (educational flow)
 * @param props - Component props
 * @param props.variant - Button style variant (contained, outlined, or text)
 * @param props.buttonColor - Custom color of the button (e.g., 'pinkPurple')
 * @param props.color - Standard MUI color (e.g., 'primary', 'secondary')
 * @param props.textColor - Color of the button text
 * @param props.size - Size of the button
 * @param props.children - Button content/label
 */
export const LearnMoreButton = ({
  variant = 'contained',
  buttonColor,
  color,
  textColor,
  size = 'large',
  children = 'Learn More',
}: LearnMoreButtonProps) => {
  const navigate = useNavigate();
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  const handleOpenOnboardingModal = () => {
    setOnboardingOpen(true);
  };

  const handleOpenInfoModal = () => {
    setInfoModalOpen(true);
  };

  const handleCloseInfoModal = () => {
    setInfoModalOpen(false);
  };

  const handleCloseOnboardingModal = () => {
    setOnboardingOpen(false);
  };

  const handleOnboardingSuccess = (_createdApp?: {
    applicationId: number;
    name: string;
    description: string;
    ownerLogin: string;
  }) => {
    // Could show success message or refresh ideas
    // createdApp data available if needed for future enhancements
  };

  const handleViewIdeas = () => {
    navigate('/action-center/my-ideas');
  };

  return (
    <>
      <DevHubButton
        variant={variant}
        {...(buttonColor && { buttonColor: buttonColor as any })}
        {...(color && { color: color as any })}
        {...(textColor && { textColor: textColor as any })}
        size={size}
        onClick={handleOpenInfoModal}
        data-gtm-event="experimental-onboarding-learn-more"
      >
        {children}
      </DevHubButton>

      <ExperimentalOnboardingModal
        open={onboardingOpen}
        onClose={handleCloseOnboardingModal}
        onSuccess={handleOnboardingSuccess}
        onViewIdeas={handleViewIdeas}
      />
      <ExperimentalOnboardingInfoModal
        open={infoModalOpen}
        onClose={handleCloseInfoModal}
        onStartOnboarding={handleOpenOnboardingModal}
      />
    </>
  );
};
